import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/api";
import { Loader2, Mail, Lock, HelpCircle, ArrowLeft, CheckCircle2 } from "lucide-react";
import Logo from "../components/Logo";
import { formatApiError } from "../lib/errors";

const ForgotPassword = () => {
  const nav = useNavigate();
  const [step, setStep] = useState(1); // 1: email, 2: question+answer+newpw, 3: success
  const [email, setEmail] = useState("");
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [newPw, setNewPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const fetchQuestion = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const res = await axios.get(`${API}/auth/security-question`, {
        params: { email: email.trim().toLowerCase() },
      });
      setQuestion(res.data.secret_question);
      setStep(2);
    } catch (e2) {
      setErr(formatApiError(e2, "No security question found for this email. Please contact admin."));
    } finally {
      setBusy(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setErr("");
    if (newPw.length < 6) {
      setErr("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email: email.trim().toLowerCase(),
        answer,
        new_password: newPw,
      });
      setStep(3);
    } catch (e2) {
      setErr(formatApiError(e2, "Reset failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#E6F7F5] via-white to-slate-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md bg-white rounded-lg shadow-xl border border-slate-100 p-8">
        <Link to="/" className="flex justify-center mb-6">
          <Logo size="lg" />
        </Link>

        {step === 3 ? (
          <div className="text-center">
            <div className="w-14 h-14 rounded-full bg-green-100 text-green-600 mx-auto flex items-center justify-center mb-3">
              <CheckCircle2 size={28} />
            </div>
            <h1 className="text-xl font-bold text-slate-800 mb-2">Password reset !</h1>
            <p className="text-sm text-slate-500 mb-6">
              You can now sign in with your new password.
            </p>
            <button
              onClick={() => nav("/login")}
              className="w-full bg-[#14B8A6] hover:bg-[#0F8A7D] text-white font-semibold uppercase tracking-wide py-2.5 rounded-md transition-colors"
            >
              Go to Sign in
            </button>
          </div>
        ) : (
          <>
            <h1 className="text-xl font-bold text-slate-800 text-center mb-1">
              Reset your password
            </h1>
            <p className="text-center text-slate-500 text-sm mb-6">
              {step === 1
                ? "Enter your registered email to continue."
                : "Answer your security question to set a new password."}
            </p>

            {step === 1 && (
              <form onSubmit={fetchQuestion} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Email
                  </label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0F2A5F] text-sm"
                      placeholder="doctor@clinic.com"
                    />
                  </div>
                </div>

                {err && (
                  <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded">
                    {err}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={busy}
                  className="w-full bg-[#14B8A6] hover:bg-[#0F8A7D] disabled:opacity-70 text-white font-semibold uppercase tracking-wide py-2.5 rounded-md flex items-center justify-center gap-2 transition-colors"
                >
                  {busy && <Loader2 size={16} className="animate-spin" />}
                  Continue
                </button>
              </form>
            )}

            {step === 2 && (
              <form onSubmit={submitReset} className="space-y-4">
                <div className="bg-[#E6F7F5] border border-[#14B8A6]/30 rounded p-3">
                  <div className="flex items-start gap-2 text-sm">
                    <HelpCircle size={16} className="text-[#14B8A6] mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700 font-medium">{question}</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Your answer
                  </label>
                  <input
                    type="text"
                    required
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    className="w-full px-3 py-2.5 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0F2A5F] text-sm"
                    placeholder="Case-insensitive answer"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    New password
                  </label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="password"
                      required
                      minLength={6}
                      value={newPw}
                      onChange={(e) => setNewPw(e.target.value)}
                      className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0F2A5F] text-sm"
                      placeholder="Minimum 6 characters"
                    />
                  </div>
                </div>

                {err && (
                  <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded">
                    {err}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={busy}
                  className="w-full bg-[#14B8A6] hover:bg-[#0F8A7D] disabled:opacity-70 text-white font-semibold uppercase tracking-wide py-2.5 rounded-md flex items-center justify-center gap-2 transition-colors"
                >
                  {busy && <Loader2 size={16} className="animate-spin" />}
                  Set new password
                </button>

                <button
                  type="button"
                  onClick={() => { setStep(1); setAnswer(""); setNewPw(""); setErr(""); }}
                  className="w-full text-sm text-slate-500 hover:text-slate-700 flex items-center justify-center gap-1"
                >
                  <ArrowLeft size={13} /> Use a different email
                </button>
              </form>
            )}

            <p className="text-center text-sm text-slate-600 mt-6">
              Remembered it?{" "}
              <Link to="/login" className="text-[#0F2A5F] font-semibold hover:underline">
                Sign in
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
